import React, { useEffect } from 'react';
import {
  Button,
  ButtonColor,
  ButtonSize,
  Header4,
  Right,
  SimpleBlock,
  SubmitButton,
  useFormContext,
  useFormContextRequired,
} from 'ui-builder';
import FormTemplate from '../../../shared/uibuilder/form/FormTemplate';
import { ColorPickerWithText } from './ColorPickerWithText';
import useClientPortalService, { Branding, UpdateThemeRequest } from '../Domain/clientPortalService';
import { DEFAULT_THEME } from './defaultTheme';
import { FileType } from "nekst-api";
import InstantImageUploaderInput from "../../../shared/uibuilder/form/input/InstantImageUploaderInput";

import styles from './ClientPortalBrandingForm.module.scss';
import { createPortal } from "react-dom";


export function ResetColorsButtonPortal() {
  return (
    <div id="reset-colors-button">
    </div>
  )
}

function ResetColorsButton() {
  const { onChangeCallback } = useFormContext();

  const element = document.getElementById('reset-colors-button');

  const handleReset = () => {
    if (onChangeCallback) {
      onChangeCallback({
        theme: DEFAULT_THEME,
      });
    }
  };

  if (element) {
    return createPortal(
      <Button
        text="Reset Colors"
        color={ButtonColor.GRAY}
        size={ButtonSize.SMALL}
        isFilled={true}
        onClick={handleReset}
      />,
      element,
    )
  } else {
    return null;
  }
}

function MobileLogoFileInput(props: {
  source: string;
  previewSource: string;
  label: string;
  fileType: FileType;
  isDisabled: (data: Branding) => boolean;
  hint?: string
}) {
  const { data, onChangeCallback } = useFormContext<Branding>();

  React.useEffect(() => {
    if (data && !data.logoFileId && (data.mobileLogoFileId || data._mobileLogoUrl)) {
      if (!onChangeCallback) {
        console.warn('onChangeCallback not available in form context');
        return;
      }
      onChangeCallback({
        mobileLogoFileId: undefined,
        _mobileLogoUrl: undefined,
      });
    }
  }, [data, onChangeCallback]);

  const disabled = data ? props.isDisabled(data) : true;

  return (
    <InstantImageUploaderInput
      fileType={props.fileType}
      previewSource={props.previewSource}
      source={props.source}
      label={props.label}
      hint={props.hint}
      isDisabled={disabled}
    />
  );
}


function ColorInputs(
  props: {
    type: 'colored' | 'white' | 'gray',
    backgroundLocked: boolean,
  }
) {

  const key = `${props.type}BackgroundBlock`;


  return (
    <SimpleBlock className={styles.rows}>
      <ColorPickerWithText
        source={`theme.${key}.backgroundColor`}
        label="Background"
        className={styles.colorInput}
        isDisabled={props.backgroundLocked}
      />

      <ColorPickerWithText
        source={`theme.${key}.fontColor`}
        label="Font Color"
        className={styles.colorInput}
      />

      <ColorPickerWithText
        source={`theme.${key}.shadingColor`}
        label="Shading Color"
        className={styles.colorInput}
      />

      <ColorPickerWithText
        source={`theme.${key}.highlightColor`}
        label="Highlight Color"
        className={styles.colorInput}
      />

      <ColorPickerWithText
        source={`theme.${key}.cardFontColor`}
        label="Card Font Color"
        className={styles.colorInput}
      />
    </SimpleBlock>
  );
}

function DataUpdater(
  props: {
    onBrandingUpdated: (data: Branding) => void;
  }
) {

  const formContext = useFormContextRequired<Branding>();

  useEffect(() => {
    props.onBrandingUpdated(formContext.data!);
  }, [JSON.stringify(formContext.data)]);

  return null;
}

export function ClientPortalLogosForm() {
  const clientPortalService = useClientPortalService();


  return (
    <FormTemplate<Branding>
      getDataFunc={async () => {
        return await clientPortalService.getBranding();
      }}
      submitFormFunc={async (data) => {
        return await clientPortalService.updateLogos({
          logoFileId: data.logoFileId,
          mobileLogoFileId: data.mobileLogoFileId,
        })
      }}
      buttons={(
        <SubmitButton />
      )}
    >
      <InstantImageUploaderInput
        fileType={FileType.COMPANY_LOGO}
        previewSource="_logoUrl"
        source="logoFileId"
        label="Logo"
        hint="Please ensure that the uploaded file has a minimum size of 300x300 pixels"
      />

      <MobileLogoFileInput
        fileType={FileType.COMPANY_LOGO}
        previewSource="_mobileLogoUrl"
        source="mobileLogoFileId"
        label="Mobile Logo (Optional)"
        isDisabled={(data) => !data.logoFileId}
        hint="Please ensure that the uploaded file has a minimum size of 300x300 pixels"
      />
    </FormTemplate>
  )
}

export default function ClientPortalThemeForm(props: {
  onBrandingUpdated: (data: Branding) => void;
}) {
  const service = useClientPortalService();

  const handleSubmit = async (formData: Branding): Promise<Branding> => {
    const request: UpdateThemeRequest = {
      theme: formData.theme,
    };

    return await service.updateBranding(request);
  };

  const getColorValidation =  (required = true) => {
    return {
      type: 'string' as const,
      constraints: {
        required,
        regex: {
          value: /^#([0-9A-Fa-f]{6}|[0-9A-Fa-f]{8})$/,
          message: 'Must be a valid hex color (#RRGGBB or #RRGGBBAA)',
        },
      },
    };
  }

  const getBlockStyleValidation = (backgroundColorRequired = true) => {
    return {
      type: 'object' as const,
      constraints: {
        required: true,
      },
      properties: {
        backgroundColor: getColorValidation(backgroundColorRequired),
        fontColor: getColorValidation(),
        shadingColor: getColorValidation(),
        highlightColor: getColorValidation(),
        cardFontColor: getColorValidation(),
      },
    };
  }

  const validationSchema = {
    theme: {
      type: 'object' as const,
      constraints: {
        required: true,
      },
      properties: {
        coloredBackgroundBlock: getBlockStyleValidation(true),
        whiteBackgroundBlock: getBlockStyleValidation(false),
        grayBackgroundBlock: getBlockStyleValidation(false),
      },
    },
  };

  return (
    <FormTemplate<UpdateThemeRequest>
      submitFormFunc={handleSubmit}
      validationSchema={validationSchema}
      getDataFunc={service.getBranding}
      initialData={undefined}
      buttons={
        <SubmitButton text="Save Changes" />
      }
    >
      <Right><ResetColorsButton /></Right>
      <Header4>Colored Background Section</Header4>
      <ColorInputs type="colored" backgroundLocked={false} />

      <Header4>White Background Section</Header4>
      <ColorInputs type="white" backgroundLocked={true} />

      <Header4>Gray Background Section</Header4>
      <ColorInputs type="gray" backgroundLocked={true} />

      <DataUpdater onBrandingUpdated={props.onBrandingUpdated} />
    </FormTemplate>
  );
}
